// FIRST, before ./App: App.js creates its router at module scope, which snapshots
// window.location on import. This module reads the landing page's PostHog handoff ids off the URL
// and strips them, and it only works if it runs before that snapshot, see utils/handoff.js.
import './utils/handoff';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './contexts/AuthContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { EnvironmentProvider } from './contexts/EnvironmentContext';
import { FlowProvider } from './contexts/FlowContext';
import { WhatsAppConnectionProvider } from './contexts/WhatsAppConnectionContext';
import { initializeFacebookSDK, validateFacebookAppConfig } from './utils/facebookSDK';
import { initErrorMonitoring } from './utils/errorMonitor';
import { initProductAnalytics } from './utils/initPostHog';
import { captureAttribution } from './utils/attribution';
import { closeIfOAuthPopup } from './utils/oauthPopup';

// Error tracking (launch workstream E). ADR-0007 chose PostHog's error tracking as the primary
// layer (see errorMonitor.js); Sentry is an additive, opt-in second capture path for richer
// stack traces / release health. Gated entirely on REACT_APP_SENTRY_DSN, with no DSN this is a
// no-op and nothing is sent, same inert-until-configured pattern as PostHog.
const initErrorTracking = (env = process.env) => {
  const dsn = env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    return false;
  }
  try {
    Sentry.init({
      dsn,
      environment: env.REACT_APP_SENTRY_ENVIRONMENT || env.NODE_ENV,
      // Tracing/session-replay are separate cost knobs from error capture; off by default.
      tracesSampleRate: 0,
    });
    return true;
  } catch (e) {
    if (env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('Sentry init failed', e);
    }
    return false;
  }
};

// Initialize Facebook SDK (non-blocking)
const initializeApp = () => {
  // The Google consent popup lands back here. It has one job left, tell the window that opened
  // it what happened, and booting the whole authenticated app inside a 520x650 window to do it
  // was how the merchant ended up staring at a miniature settings page (2026-09-23). Checked
  // before anything else starts, so nothing else starts.
  const popupOutcome = closeIfOAuthPopup();
  if (popupOutcome) {
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(<OAuthPopupClosing result={popupOutcome.result} />);
    return;
  }
  // Read whatever first-touch attribution the landing page's appUrl() appended to this URL
  // before anything else, so it's ready for the very first tracked event. Never strips the URL
  // (no history churn), see utils/attribution.js.
  captureAttribution();
  // Wire the analytics provider first so client errors captured below are forwarded to it.
  initProductAnalytics();
  initErrorTracking();
  initErrorMonitoring();
  const root = ReactDOM.createRoot(document.getElementById('root'));

  // Render the app with AuthProvider as outermost context
  // PermissionsProvider comes after OrganizationProvider since it depends on selected org
  root.render(
    <AuthProvider>
      <EntitlementsProvider>
      <PreferencesProvider>
        <OrganizationProvider>
          <PermissionsProvider>
            <EnvironmentProvider>
              <WhatsAppConnectionProvider>
                <FlowProvider>
                  <App />
                </FlowProvider>
              </WhatsAppConnectionProvider>
            </EnvironmentProvider>
          </PermissionsProvider>
        </OrganizationProvider>
      </PreferencesProvider>
      </EntitlementsProvider>
    </AuthProvider>
  );

  // Initialize Facebook SDK in the background (non-blocking)
  const isConfigValid = validateFacebookAppConfig();

  if (isConfigValid) {
    initializeFacebookSDK()
      .catch((error) => {
        console.warn('Facebook SDK initialization failed:', error.message);
      });
  }
};

/**
 * What the popup shows in the moment before it closes, and what it keeps showing in a browser
 * that refuses `window.close()`, where the alternative is a blank white window.
 */
const POPUP_MESSAGES = {
  connected: 'Connected. You can close this window and go back to FlowGate.',
  denied: 'No access was granted. You can close this window and go back to FlowGate.',
  failed: 'Something went wrong connecting your Google account. Close this window and try again.',
};

function OAuthPopupClosing({ result }) {
  return (
    <div style={{
      fontFamily: 'system-ui, -apple-system, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', margin: 0, padding: '0 24px', textAlign: 'center', color: '#0b0d12',
    }}>
      <p>{POPUP_MESSAGES[result] || POPUP_MESSAGES.connected}</p>
    </div>
  );
}

// Initialize the app
initializeApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
